<template>
  <button
    class="data-center-card"
    type="button"
    aria-haspopup="dialog"
    :aria-label="ariaLabel"
    :style="themeStyle"
    @click="emit('open-theme-drawer')"
  >
    <span class="data-center-card__content">
      <span class="data-center-card__header">
        <span class="data-center-card__title">{{ title }}</span>
        <span class="data-center-card__status">
          <span class="data-center-card__status-dot"></span>
          {{ statusLabel }}
        </span>
      </span>
      <span class="data-center-card__label">{{ updateLabel }}</span>
      <span class="data-center-card__date">{{ updateDate }}</span>
    </span>

    <img
      v-if="artworkVisible"
      class="data-center-card__artwork"
      :src="theme.asset"
      alt=""
      aria-hidden="true"
      @error="handleArtworkError"
    />
  </button>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { getDataCenterThemeStyle } from '@/config/dataCenterThemes.mjs';

const props = defineProps({
  theme: { type: Object, required: true },
  title: { type: String, required: true },
  statusLabel: { type: String, required: true },
  updateLabel: { type: String, required: true },
  updateDate: { type: String, required: true },
  ariaLabel: { type: String, default: '打开主题选择' },
});

const emit = defineEmits(['open-theme-drawer', 'artwork-error']);
const artworkVisible = ref(true);
const themeStyle = computed(() => getDataCenterThemeStyle(props.theme));

watch(
  () => props.theme.asset,
  () => { artworkVisible.value = true; },
);

function handleArtworkError() {
  artworkVisible.value = false;
  emit('artwork-error');
}
</script>

<style scoped>
.data-center-card {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 112px;
  overflow: hidden;
  padding: 14px;
  appearance: none;
  border: 1px solid rgba(220, 232, 245, 0.55);
  border-radius: 16px;
  background:
    radial-gradient(circle at 82% 82%, var(--dc-theme-glow) 0%, var(--dc-theme-soft-glow) 38%, rgba(255, 255, 255, 0) 68%),
    linear-gradient(145deg, #ffffff 0%, #fdfefe 68%, var(--dc-theme-tail) 100%);
  box-shadow:
    0 10px 28px rgba(54, 105, 160, 0.08),
    0 2px 6px rgba(54, 105, 160, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.96);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.data-center-card:hover {
  transform: translateY(-1px);
  border-color: rgba(190, 218, 241, 0.68);
  box-shadow: 0 13px 30px rgba(54, 105, 160, 0.1), 0 3px 7px rgba(54, 105, 160, 0.05);
}

.data-center-card:focus-visible {
  outline: 2px solid rgba(34, 183, 237, 0.72);
  outline-offset: 2px;
}

.data-center-card__content {
  position: relative;
  z-index: 2;
  display: block;
  width: 92px;
}

.data-center-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 132px;
}

.data-center-card__title {
  color: #4f647b;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.data-center-card__status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #18a968;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.data-center-card__status-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #20c77a;
  box-shadow: 0 0 8px rgba(32, 199, 122, 0.48);
}

.data-center-card__label {
  display: block;
  margin-top: 18px;
  color: #45a76c;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.data-center-card__date {
  display: block;
  margin-top: 3px;
  color: #101828;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.data-center-card__artwork {
  position: absolute;
  right: var(--dc-artwork-right);
  bottom: var(--dc-artwork-bottom);
  z-index: 0;
  width: var(--dc-artwork-width);
  max-width: none;
  height: auto;
  border: 0;
  background: transparent;
  object-fit: contain;
  object-position: right bottom;
  transform: scaleX(var(--dc-artwork-scale-x));
  transform-origin: right bottom;
  filter: var(--dc-artwork-filter);
  pointer-events: none;
  user-select: none;
}

:global(html.dark .data-center-card) {
  border-color: var(--wf-border);
  background:
    radial-gradient(circle at 82% 82%, var(--dc-theme-glow) 0%, var(--dc-theme-soft-glow) 32%, rgba(17, 24, 39, 0) 64%),
    linear-gradient(145deg, var(--wf-surface-elevated) 0%, var(--wf-surface) 70%, #17243a 100%);
  box-shadow: 0 10px 28px var(--wf-shadow), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

:global(html.dark .data-center-card:hover) {
  border-color: rgba(125, 211, 252, 0.38);
  box-shadow: 0 13px 30px var(--wf-shadow);
}

:global(html.dark .data-center-card__title) {
  color: var(--wf-text-muted);
}

:global(html.dark .data-center-card__date) {
  color: var(--wf-text-primary);
}
</style>
